import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiZap, FiCheck, FiPhone, FiArrowRight, FiInfo } = FiIcons;

const services = [
  {
    category: 'Electrical',
    icon: '⚡',
    color: 'from-blue-600 to-cyan-600',
    items: [
      { name: 'New House Wiring', price: '₹8,000+', unit: 'per room', note: 'Includes switchboards, MCB, earthing' },
      { name: 'Rewiring (existing)', price: '₹5,000+', unit: 'per room', note: 'Complete rewiring with new cables' },
      { name: 'Switchboard Replacement', price: '₹500+', unit: 'per board', note: 'Standard modular switchboards' },
      { name: 'MCB/DB Installation', price: '₹2,500+', unit: 'per DB', note: 'With circuit protection' },
      { name: 'Emergency Repair', price: '₹500+', unit: 'visit charge', note: 'Adjusted in final bill' },
    ]
  },
  {
    category: 'CCTV & Security',
    icon: '📷',
    color: 'from-purple-600 to-pink-600',
    items: [
      { name: '2 Camera CCTV Package', price: '₹8,000+', unit: 'complete', note: 'HD cameras + DVR + cables + installation' },
      { name: '4 Camera CCTV Package', price: '₹14,000+', unit: 'complete', note: 'HD cameras + DVR + remote access setup' },
      { name: '8 Camera CCTV Package', price: '₹25,000+', unit: 'complete', note: 'Full HD + 4K options available' },
      { name: 'Smart Lock Installation', price: '₹3,500+', unit: 'per door', note: 'Biometric or PIN based' },
      { name: 'Alarm System', price: '₹5,000+', unit: 'complete', note: 'Motion sensors + siren + mobile alert' },
    ]
  },
  {
    category: 'Solar Power',
    icon: '☀️',
    color: 'from-yellow-500 to-orange-500',
    items: [
      { name: '1 kW Solar System', price: '₹60,000+', unit: 'complete', note: 'For small homes, 4-5 hours backup' },
      { name: '2 kW Solar System', price: '₹1,10,000+', unit: 'complete', note: 'For medium homes, all-day power' },
      { name: '5 kW Solar System', price: '₹2,50,000+', unit: 'complete', note: 'For large homes/shops' },
      { name: 'Solar Site Survey', price: 'FREE', unit: '', note: 'Assessment + recommendation report' },
    ]
  },
  {
    category: 'Smart Home',
    icon: '🏠',
    color: 'from-green-600 to-teal-600',
    items: [
      { name: 'Smart Lighting Setup', price: '₹8,000+', unit: 'per room', note: 'App + voice control' },
      { name: 'Home Automation Package', price: '₹35,000+', unit: '3 rooms', note: 'Lights, fans, AC control' },
      { name: 'Smart Curtain/Blind Motor', price: '₹4,500+', unit: 'per window', note: 'App & remote controlled' },
      { name: 'Video Door Phone', price: '₹6,000+', unit: 'complete', note: 'See & speak from your phone' },
    ]
  },
  {
    category: 'Inverter & UPS',
    icon: '🔋',
    color: 'from-indigo-600 to-purple-600',
    items: [
      { name: 'Home Inverter Installation', price: '₹2,000+', unit: 'installation', note: 'Any brand, any capacity' },
      { name: 'UPS for Office/Clinic', price: '₹3,500+', unit: 'installation', note: 'Includes wiring & battery setup' },
      { name: 'Battery Replacement', price: '₹500+', unit: 'labour', note: 'Battery cost extra' },
      { name: 'Inverter Service/Repair', price: '₹800+', unit: 'per visit', note: 'Diagnosis + repair' },
    ]
  },
];

const amc = [
  { name: 'Basic', price: '₹999', period: '/year', color: 'from-gray-700 to-gray-600', features: ['Annual inspection', 'Priority booking', '2 free service calls', 'WhatsApp support'], popular: false },
  { name: 'Standard', price: '₹1,999', period: '/year', color: 'from-blue-700 to-cyan-700', features: ['Bi-annual inspection', 'Priority 24/7 booking', '5 free service calls', 'Free minor repairs', 'WhatsApp + call support'], popular: true },
  { name: 'Premium', price: '₹2,999', period: '/year', color: 'from-purple-700 to-pink-700', features: ['Quarterly inspection', 'Emergency response <1hr', 'Unlimited service calls', 'Free minor + medium repairs', 'Dedicated support line', 'Annual safety audit'], popular: false },
];

const Pricing = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', ...services.map(s => s.category)];
  const filtered = activeCategory === 'All' ? services : services.filter(s => s.category === activeCategory);

  return (
    <div className="min-h-screen bg-black pt-20">
      <Helmet>
        <title>Service Pricing — Tayyab Electric Studio Baramulla</title>
        <meta name="description" content="Transparent pricing for electrical installation, CCTV, solar power, smart home automation in Baramulla Kashmir. Starting prices with no hidden charges." />
      </Helmet>

      {/* Hero */}
      <section className="py-14 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-black to-purple-900/10" />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center space-x-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-2 text-sm text-blue-300 mb-4">
              <SafeIcon icon={FiZap} /><span>Transparent Pricing</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
              Service <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Pricing</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-4">Starting prices for all our services. Final quote provided after site visit — no hidden charges, ever.</p>
            <div className="inline-flex items-center space-x-2 bg-yellow-900/20 border border-yellow-700/30 rounded-xl px-4 py-2 text-yellow-300 text-sm">
              <SafeIcon icon={FiInfo} /><span>All prices include labour. Materials quoted separately.</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Category filter */}
      <section className="max-w-6xl mx-auto px-4 mb-8">
        <div className="flex flex-wrap gap-2 justify-center">
          {categories.map(c => (
            <button key={c} onClick={() => setActiveCategory(c)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${activeCategory === c ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'}`}>
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Service pricing cards */}
      <section className="max-w-6xl mx-auto px-4 pb-16 space-y-8">
        {filtered.map((svc, si) => (
          <motion.div key={svc.category} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: si * 0.1 }}
            className="bg-gray-900/60 border border-gray-800 rounded-3xl overflow-hidden">
            <div className={`bg-gradient-to-r ${svc.color} px-6 py-4 flex items-center space-x-3`}>
              <span className="text-2xl">{svc.icon}</span>
              <h2 className="text-white font-black text-xl">{svc.category}</h2>
            </div>
            <div className="divide-y divide-gray-800/50">
              {svc.items.map((item, i) => (
                <div key={i} className="flex items-center justify-between px-6 py-4 hover:bg-gray-800/30 transition-colors">
                  <div>
                    <p className="text-white font-medium">{item.name}</p>
                    <p className="text-gray-500 text-xs mt-0.5">{item.note}</p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-white font-black text-lg">{item.price}</p>
                    {item.unit && <p className="text-gray-500 text-xs">{item.unit}</p>}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </section>

      {/* AMC Plans */}
      <section className="max-w-6xl mx-auto px-4 pb-20">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-black text-white mb-2">Annual Maintenance Plans</h2>
          <p className="text-gray-400">Choose a plan that keeps your electrical systems running perfectly all year.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {amc.map((plan, i) => (
            <motion.div key={plan.name} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
              className={`relative bg-gray-900 border rounded-3xl overflow-hidden ${plan.popular ? 'border-blue-500/60 shadow-xl shadow-blue-500/10' : 'border-gray-800'}`}>
              {plan.popular && (
                <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-xs font-bold text-center py-1.5">⭐ MOST POPULAR</div>
              )}
              <div className={`bg-gradient-to-r ${plan.color} px-6 py-5`}>
                <h3 className="text-white font-black text-xl">{plan.name}</h3>
                <div className="flex items-end space-x-1 mt-1">
                  <span className="text-3xl font-black text-white">{plan.price}</span>
                  <span className="text-white/70 text-sm mb-1">{plan.period}</span>
                </div>
              </div>
              <div className="p-6 space-y-3">
                {plan.features.map((f, j) => (
                  <div key={j} className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheck} className="text-green-400 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{f}</span>
                  </div>
                ))}
                <Link to="/appointments"
                  className={`mt-4 w-full flex items-center justify-center space-x-2 py-3 rounded-2xl font-bold text-sm transition-all ${plan.popular ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:from-blue-700 hover:to-cyan-700' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}>
                  <span>Get {plan.name} Plan</span><SafeIcon icon={FiArrowRight} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-4 pb-20 text-center">
        <div className="bg-gradient-to-br from-blue-900/30 to-cyan-900/20 border border-blue-800/30 rounded-3xl p-8">
          <h3 className="text-white font-black text-2xl mb-2">Need a Custom Quote?</h3>
          <p className="text-gray-400 mb-6">Every project is unique. Get a free site visit and detailed estimate with no obligation.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
            <Link to="/appointments" className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-3 rounded-2xl font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all">
              <span>Book Free Site Visit</span><SafeIcon icon={FiArrowRight} />
            </Link>
            <a href="tel:+919906669918" className="flex items-center space-x-2 border border-gray-700 text-gray-300 px-6 py-3 rounded-2xl font-semibold hover:border-blue-500 transition-all">
              <SafeIcon icon={FiPhone} /><span>+91 99066 69918</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;